import { Fragment, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, Check, Upload, X } from 'lucide-react';
import { toast } from 'sonner';

import {
  apiAdminshopsPost,
  apiAdminshopWorkspacesIdPatch,
  apiAdminshopWorkspacesPost,
  apiMediasPost,
  apiShopsIdGet,
  getApiShopsGetCollectionQueryKey
} from '@r/api/generated/client.ts';
import { ShopShopWriteModelType } from '@r/api/generated/client.schemas.ts';
import type { ShopShopReadModel } from '@r/api/generated/client.schemas.ts';
import {
  toUtcEndOfDay,
  toUtcStartOfDay,
  fromDateInputValue,
  getStartOfToday,
  toDateInputString
} from '@r/utils/date-utils.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { defineStepper } from '@ui/stepper.tsx';
import { Button } from '@ui/button.tsx';
import { DatePicker } from '@ui/date-picker.tsx';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { Input } from '@ui/input.tsx';
import { Textarea } from '@ui/textarea.tsx';
import { Switch } from '@ui/switch.tsx';
import { RadioGroup, RadioGroupItem } from '@ui/radio-group.tsx';
import { Label } from '@ui/label.tsx';
import {
  buildCreateCustomShopFormBaseSchema,
  buildCreateCustomShopFormSchema,
  buildRequiredMinPointSchema,
  PERSONALIZATION_FIELDS,
  type CreateCustomShopFormValues
} from './create-custom-shop-form-schema.ts';

const { StepperProvider, StepperNavigation, StepperStep, StepperTitle, StepperPanel } =
  defineStepper(
    { id: 'personalization', title: 'admin.shops.create.step.personalization' },
    { id: 'opening', title: 'admin.shops.create.step.opening' }
  );

interface CreateCustomShopFormProps {
  onCancel: () => void;
  onSuccess: () => void;
}

/** Génère l'identifiant technique attendu par l'API à partir du nom affiché. */
function buildTechnicalName(displayName: string): string {
  const slug = displayName
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

  return (slug || displayName.trim()).slice(0, 100);
}

/**
 * Orchestre la création d'une boutique custom en deux étapes : création de la
 * boutique et de ses médias, puis configuration de son workspace.
 */
export function CreateCustomShopForm({ onCancel, onSuccess }: CreateCustomShopFormProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const showOrvalErrorToast = useOrvalErrorToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const baseSchema = useMemo(() => buildCreateCustomShopFormBaseSchema(t), [t]);
  const formSchema = useMemo(() => buildCreateCustomShopFormSchema(t), [t]);
  const requiredMinPointSchema = useMemo(() => buildRequiredMinPointSchema(t), [t]);

  const form = useForm<CreateCustomShopFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      displayName: '',
      description: '',
      banner: undefined,
      operationalContact: '',
      technicalContact: '',
      isActive: true,
      shopOpeningDate: '',
      shopClosingDate: '',
      hasMinPoint: false,
      minPoint: undefined
    },
    mode: 'onChange'
  });

  const hasMinPoint = form.watch('hasMinPoint');

  const handleCancel = () => {
    form.reset();
    onCancel();
  };

  const uploadMedia = async (file: File): Promise<string> => {
    const media = await apiMediasPost({ file, type: 'layout' });
    if (!media.iri) throw new Error('Missing media IRI');
    return media.iri;
  };

  const submit = async (values: CreateCustomShopFormValues) => {
    setIsSubmitting(true);

    try {
      // Le logo et la bannière doivent être créés avant la boutique, qui référence leurs IRI.
      const [logo, banner] = await Promise.all([
        uploadMedia(values.logo),
        values.banner ? uploadMedia(values.banner) : Promise.resolve(null)
      ]);

      const shop = await apiAdminshopsPost({
        name: buildTechnicalName(values.displayName),
        displayName: values.displayName.trim(),
        description: values.description.trim(),
        type: ShopShopWriteModelType.custom,
        logo,
        banner,
        operationalContact: values.operationalContact.trim(),
        technicalContact: values.technicalContact.trim()
      });

      let shopWorkspaceId = shop.shopWorkspace?.id;

      // Le workspace porte les règles d'ouverture et peut ne pas être créé avec la boutique.
      if (!shopWorkspaceId) {
        try {
          const shopWorkspace = await apiAdminshopWorkspacesPost({ shop: `/shops/${shop.id}` });
          shopWorkspaceId = shopWorkspace.id;
        } catch (linkError) {
          // Certaines versions du back créent le rattachement avec la boutique sans
          // toujours le sérialiser dans la réponse du POST.
          let refreshedShop: ShopShopReadModel;
          try {
            refreshedShop = await apiShopsIdGet(String(shop.id));
          } catch {
            throw linkError;
          }

          if (!refreshedShop.shopWorkspace) throw linkError;
          shopWorkspaceId = refreshedShop.shopWorkspace.id;
        }
      }

      await apiAdminshopWorkspacesIdPatch(String(shopWorkspaceId), {
        isActive: values.isActive,
        shopOpeningDate: (() => {
          const openingDate = fromDateInputValue(values.shopOpeningDate);
          return openingDate ? toUtcStartOfDay(openingDate).toISOString() : null;
        })(),
        shopClosingDate: (() => {
          const closingDate = fromDateInputValue(values.shopClosingDate);
          return closingDate ? toUtcEndOfDay(closingDate).toISOString() : null;
        })(),
        minPoint: values.hasMinPoint ? values.minPoint : undefined
      });

      await queryClient.invalidateQueries({ queryKey: getApiShopsGetCollectionQueryKey() });
      toast.success(t('admin.shops.create.success'));
      form.reset();
      onSuccess();
    } catch (error) {
      showOrvalErrorToast(error, t('admin.shops.create.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form} schema={baseSchema}>
      <StepperProvider variant="horizontal" labelOrientation="vertical" className="w-full">
        {({ methods }) => {
          const isFirstStep = methods.current.id === 'personalization';

          const handleNext = async () => {
            if (isFirstStep) {
              // Ne valide que les champs de l'étape courante avant d'autoriser la navigation.
              const isValid = await form.trigger(PERSONALIZATION_FIELDS);
              if (isValid) methods.next();
              return;
            }

            await form.handleSubmit(submit)();
          };

          return (
            <Fragment>
              <StepperNavigation className="mb-8">
                {methods.all.map((step) => (
                  <StepperStep key={step.id} of={step.id} type="button">
                    <StepperTitle>{t(step.title)}</StepperTitle>
                  </StepperStep>
                ))}
              </StepperNavigation>

              <div className="mb-8">
                <h2 className="text-2xl font-bold">{t('admin.shops.create.dialog.title')}</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  {t('admin.shops.create.dialog.description')}
                </p>
              </div>

              {methods.switch({
                personalization: () => (
                  <StepperPanel key="personalization" className="space-y-6">
                    <FormField
                      control={form.control}
                      name="displayName"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel>{t('admin.shops.create.form.display.name.label')}</FormLabel>
                          <FormControl>
                            <Input
                              placeholder={t('admin.shops.create.form.display.name.placeholder')}
                              maxLength={100}
                              error={!!fieldState.error}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel>{t('admin.shops.create.form.description.label')}</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder={t('admin.shops.create.form.description.placeholder')}
                              maxLength={255}
                              rows={3}
                              error={!!fieldState.error}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="logo"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel htmlFor="create-custom-shop-logo">
                              {t('admin.shops.create.form.logo.label')}
                            </FormLabel>
                            <p className="text-xs text-muted-foreground">
                              {t('admin.shops.create.form.logo.hint')}
                            </p>
                            <FormControl>
                              <label className="flex min-h-24 cursor-pointer items-center justify-center gap-3 rounded-md border border-dashed bg-muted/30 p-4 text-center text-sm">
                                <Upload className="size-4 shrink-0" />
                                <span className="break-all">
                                  {field.value?.name ?? t('admin.shops.create.form.logo.upload')}
                                </span>
                                <input
                                  id="create-custom-shop-logo"
                                  className="sr-only"
                                  type="file"
                                  accept="image/*"
                                  onChange={(event) => field.onChange(event.target.files?.[0])}
                                />
                              </label>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="banner"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel htmlFor="create-custom-shop-banner">
                              {t('admin.shops.create.form.banner.label')}
                            </FormLabel>
                            <p className="text-xs text-muted-foreground">
                              {t('admin.shops.create.form.banner.hint')}
                            </p>
                            <FormControl>
                              <label className="flex min-h-24 cursor-pointer items-center justify-center gap-3 rounded-md border border-dashed bg-muted/30 p-4 text-center text-sm">
                                <Upload className="size-4 shrink-0" />
                                <span className="break-all">
                                  {field.value?.name ?? t('admin.shops.create.form.banner.upload')}
                                </span>
                                <input
                                  id="create-custom-shop-banner"
                                  className="sr-only"
                                  type="file"
                                  accept="image/*"
                                  onChange={(event) => field.onChange(event.target.files?.[0])}
                                />
                              </label>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </StepperPanel>
                ),
                opening: () => (
                  <StepperPanel key="opening" className="space-y-6">
                    <FormField
                      control={form.control}
                      name="isActive"
                      render={({ field }) => (
                        <FormItem className="flex items-center justify-between">
                          <FormLabel>{t('admin.shops.create.form.status.label')}</FormLabel>
                          <div className="flex items-center gap-3">
                            <FormControl>
                              <Switch checked={field.value} onCheckedChange={field.onChange} />
                            </FormControl>
                            <span className="text-sm">
                              {field.value ? t('label.shop.active') : t('label.shop.disabled')}
                            </span>
                          </div>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="operationalContact"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel>
                            {t('admin.shops.create.form.operational.contact.label')}
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder={t('admin.shops.form.email.placeholder')}
                              error={!!fieldState.error}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="technicalContact"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel>
                            {t('admin.shops.create.form.technical.contact.label')}
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder={t('admin.shops.form.email.placeholder')}
                              error={!!fieldState.error}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="shopOpeningDate"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('label.shop.opening.date')}</FormLabel>
                            <FormControl>
                              <DatePicker
                                value={field.value}
                                onChange={(date) =>
                                  field.onChange(date ? toDateInputString(date) : '')
                                }
                                isDateDisabled={(date) => {
                                  const closingDate = fromDateInputValue(
                                    form.getValues('shopClosingDate')
                                  );

                                  return (
                                    date < getStartOfToday() ||
                                    (!!closingDate && date >= closingDate)
                                  );
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="shopClosingDate"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('label.shop.closing.date')}</FormLabel>
                            <FormControl>
                              <DatePicker
                                value={field.value}
                                onChange={(date) =>
                                  field.onChange(date ? toDateInputString(date) : '')
                                }
                                isDateDisabled={(date) => {
                                  const openingDate = fromDateInputValue(
                                    form.getValues('shopOpeningDate')
                                  );

                                  return (
                                    date < getStartOfToday() ||
                                    (!!openingDate && date <= openingDate)
                                  );
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="hasMinPoint"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('admin.shops.create.form.has.min.point.label')}</FormLabel>
                          <FormControl>
                            <RadioGroup
                              className="flex gap-6"
                              value={field.value ? 'yes' : 'no'}
                              onValueChange={(value) => field.onChange(value === 'yes')}
                            >
                              <div className="flex items-center gap-2">
                                <RadioGroupItem id="min-point-yes" value="yes" />
                                <Label htmlFor="min-point-yes">
                                  {t('admin.shops.create.form.min.point.yes')}
                                </Label>
                              </div>
                              <div className="flex items-center gap-2">
                                <RadioGroupItem id="min-point-no" value="no" />
                                <Label htmlFor="min-point-no">
                                  {t('admin.shops.create.form.min.point.no')}
                                </Label>
                              </div>
                            </RadioGroup>
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    {hasMinPoint && (
                      <FormField
                        control={form.control}
                        name="minPoint"
                        fieldSchema={requiredMinPointSchema}
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <FormLabel>{t('admin.shops.create.form.min.point.label')}</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                min={0}
                                placeholder={t('admin.shops.create.form.min.point.placeholder')}
                                error={!!fieldState.error}
                                value={field.value ?? ''}
                                onChange={(event) =>
                                  field.onChange(
                                    event.target.value === ''
                                      ? undefined
                                      : event.target.valueAsNumber
                                  )
                                }
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                  </StepperPanel>
                )
              })}

              <div className="mt-10 flex gap-4">
                <Button
                  type="button"
                  variant="outline"
                  rounded="full"
                  className="flex-1"
                  disabled={isSubmitting}
                  onClick={isFirstStep ? handleCancel : methods.prev}
                >
                  {isFirstStep ? <X className="size-4" /> : <ArrowLeft className="size-4" />}
                  {isFirstStep ? t('label.cancel') : t('admin.shops.create.form.back')}
                </Button>
                <Button
                  type="button"
                  rounded="full"
                  className="flex-1"
                  loading={isSubmitting}
                  onClick={() => void handleNext()}
                >
                  {isFirstStep ? (
                    <>
                      {t('admin.shops.create.form.next')} <ArrowRight className="size-4" />
                    </>
                  ) : (
                    <>
                      {t('admin.shops.create.form.submit')} <Check className="size-4" />
                    </>
                  )}
                </Button>
              </div>
            </Fragment>
          );
        }}
      </StepperProvider>
    </Form>
  );
}
